import type { TrackingCompletionData } from "../components/NotesDialog";

import { core } from "@tauri-apps/api";
import { createContext, useCallback, useContext, useMemo, useState } from "react";

import { debugProductivity } from "@/domains/logging/utils/debugLog";

import { useBuzzerControl } from "../hooks/time_tracking/useBuzzerControl";
import { useTimeTracqControl } from "../hooks/time_tracking/useTimeTracqControl";
import { useWorkspaceConfig } from "../hooks/time_tracking/useWorkspaceConfig";

interface TrackingSession {
  aspect: string;
  created_at?: string;
  efficiency_rating?: null | number;
  end_time?: null | string;
  id: string;
  impact_score?: null | string;
  notes?: null | string;
  start_time: string;
  task_id?: null | string;
  what_was_inefficient?: null | string;
  what_went_well?: null | string;
  workspace: string;
}

// eslint-disable-next-line import/no-unused-modules -- Reason: Type export for external use and type safety
export interface TimeTracQContextType {
  config: Record<string, Record<string, unknown>>;
  confirmedWorkspace: null | string;
  currentOptions: string[];
  currentSelectionIndex: number;
  handleTrackingCompletion: (data: TrackingCompletionData) => Promise<void>;
  isLoading: boolean;
  isTracking: boolean;
  lastHeartbeat: number;
  mode: "aspect" | "workspace";
  proximityState: boolean;
  session: null | TrackingSession;
  setShowCompletionDialog: (show: boolean) => void;
  showCompletionDialog: boolean;
  startTracking: (workspace: string, aspect: string, color?: string) => void;
  stopTracking: () => void;
}

// eslint-disable-next-line import/no-unused-modules -- Reason: Type export for external use and type safety
export interface TimeTracQLiveContextType {
  lastHeartbeat: number;
  proximityState: boolean;
}

export const TimeTracQContext = createContext<null | TimeTracQContextType>(null);
export const TimeTracQLiveContext = createContext<null | TimeTracQLiveContextType>(null);

export const useTimeTracQContext = (): TimeTracQContextType => {
  const context = useContext(TimeTracQContext);
  if (!context) {
    throw new Error("useTimeTracQContext must be used within a TimeTracQProvider");
  }
  return context;
};

export const useTimeTracQLiveContext = (): TimeTracQLiveContextType => {
  const context = useContext(TimeTracQLiveContext);
  if (!context) {
    throw new Error("useTimeTracQLiveContext must be used within a TimeTracQProvider");
  }
  return context;
};

export const useTimeTracQState = (): {
  stableValue: TimeTracQContextType;
  liveValue: TimeTracQLiveContextType;
} => {
  const [config, , isLoading] = useWorkspaceConfig();
  const { isTracking, session, startTracking } = useTimeTracqControl();
  const [showCompletionDialog, setShowCompletionDialog] = useState(false);

  const {
    confirmedWorkspace,
    currentOptions,
    currentSelectionIndex,
    lastHeartbeat,
    mode,
    proximityState,
  } = useBuzzerControl({
    config,
    isTracking,
    startTracking,
    stopTracking: () => {
      setShowCompletionDialog(true);
    },
  });

  const handleTrackingCompletion = useCallback(
    async (data: TrackingCompletionData) => {
      if (!session) {
        debugProductivity(
          "warn",
          "timetracq",
          "Tracking completion called but no active session",
          {},
        );
        return;
      }

      debugProductivity("info", "timetracq", "Completing tracking session", {
        has_efficiency_rating:
          data.efficiencyRating !== null && data.efficiencyRating !== undefined,
        has_notes: !!data.notes,
        has_what_was_inefficient: !!data.whatWasInefficient,
        has_what_went_well: !!data.whatWentWell,
        session_id: session.id,
      });

      try {
        await core.invoke("update_tracking_session", {
          efficiencyRating: data.efficiencyRating ?? null,
          endTime: new Date().toISOString(),
          id: session.id,
          impactScore: data.impactScore ?? null,
          notes: data.notes ?? null,
          taskId: data.taskId ?? null,
          whatWasInefficient: data.whatWasInefficient ?? null,
          whatWentWell: data.whatWentWell ?? null,
        });

        debugProductivity("info", "timetracq", "Tracking session completed successfully", {
          session_id: session.id,
        });

        setShowCompletionDialog(false);
      } catch (error) {
        debugProductivity("error", "timetracq", "Error completing tracking session", {
          error: error instanceof Error ? error.message : String(error),
          session_id: session.id,
        });
      }
    },
    [session],
  );

  const stopTracking = useCallback(() => {
    if (!session) {
      debugProductivity("warn", "timetracq", "Stop tracking called but no active session", {});
      return;
    }
    setShowCompletionDialog(true);
  }, [session]);

  const stableValue: TimeTracQContextType = useMemo(
    () => ({
      config,
      confirmedWorkspace,
      currentOptions,
      currentSelectionIndex,
      handleTrackingCompletion,
      isLoading,
      isTracking,
      lastHeartbeat: 0,
      mode,
      proximityState: false,
      session,
      setShowCompletionDialog,
      showCompletionDialog,
      startTracking,
      stopTracking,
    }),
    [
      config,
      isLoading,
      isTracking,
      startTracking,
      stopTracking,
      session,
      mode,
      currentOptions,
      currentSelectionIndex,
      confirmedWorkspace,
      showCompletionDialog,
      handleTrackingCompletion,
    ],
  );

  const liveValue: TimeTracQLiveContextType = useMemo(
    () => ({
      lastHeartbeat,
      proximityState,
    }),
    [lastHeartbeat, proximityState],
  );

  return { liveValue, stableValue };
};
